import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Stack,
  Alert,
  CircularProgress,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  StoreMallDirectory as WalkInIcon,
  Event as EventIcon,
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { useSnackbar } from 'notistack';
import {
  refillRequestService,
  RefillRequestChannel,
} from '../../services/refillRequestService';
import { medicationService } from '../../services/medicationService';
import api, { horseApi, appointmentApi, Horse } from '../../utils/api';
import { patientLabel } from '../../utils/patientLabel';
import { skipWhenOffline } from '../../offline/offlineQuery';
import { useFormatInstant } from '../../hooks/useClinicTimezone';

interface Veterinarian {
  id: string;
  name: string;
  firstName?: string;
  lastName?: string;
  professionalTitle?: string;
}

/** Build a display name like "Dr. Jane Smith" with sensible fallbacks.
 * Avoids double-titling when the stored name already starts with "Dr." (a
 * common case for seeded users where the title lives in the name field). */
const formatVetName = (v: Veterinarian): string => {
  const full =
    v.firstName || v.lastName
      ? `${v.firstName ?? ''} ${v.lastName ?? ''}`.trim()
      : v.name;
  if (/^(dr\.?|prof\.?|mr\.?|ms\.?|mrs\.?)\s/i.test(full)) return full;
  const title = v.professionalTitle || 'Dr.';
  return `${title} ${full}`.trim();
};

/** The next-visit pattern this dialog has always shown — wall-clock tokens only. */
const NEXT_VISIT_FORMAT = 'EEE MMM d, h:mm a';

interface LogRefillRequestDialogProps {
  open: boolean;
  onClose: () => void;
  /** When provided, pre-selects the horse and hides the horse picker. */
  preselectedHorseId?: string;
  /** Called after a successful submit so the parent can refresh its list. */
  onCreated?: () => void;
}

/**
 * Staff-facing intake for a refill request — used when an owner calls,
 * walks in, or staff is logging a request from the patient profile. Email
 * and SMS channels are reserved for inbound webhooks (Postmark/Twilio,
 * future) so this dialog only exposes PHONE / WALK_IN.
 *
 * Light appointment context: once a horse is picked the dialog shows the
 * horse's next upcoming visit if any, so staff can decide whether to log
 * the refill or just hold it for the visit.
 */
const LogRefillRequestDialog: React.FC<LogRefillRequestDialogProps> = ({
  open,
  onClose,
  preselectedHorseId,
  onCreated,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  // Appointment.date is an INSTANT (DATE-KINDS §1.3 — appointments.controller.ts
  // converts the posted wall clock through its timeZone to UTC). Render it in the
  // CLINIC's zone, not the browser's: staff logging a refill from home must read
  // the same visit time the schedule shows.
  const formatInstant = useFormatInstant();

  const [channel, setChannel] = useState<RefillRequestChannel>('PHONE');
  const [selectedHorse, setSelectedHorse] = useState<Horse | null>(null);
  const [medicationId, setMedicationId] = useState<string>('');
  const [assignedVetId, setAssignedVetId] = useState<string>('');
  const [ownerName, setOwnerName] = useState('');
  const [ownerContact, setOwnerContact] = useState('');
  const [rawIntakeBody, setRawIntakeBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state on close so the next open starts fresh.
  useEffect(() => {
    if (!open) {
      setChannel('PHONE');
      setSelectedHorse(null);
      setMedicationId('');
      setAssignedVetId('');
      setOwnerName('');
      setOwnerContact('');
      setRawIntakeBody('');
      setError(null);
    }
  }, [open]);

  // Vet picker — optional routing hint. Any vet can still decide the request,
  // but assigning at intake tags it for that vet's queue (e.g. owner's
  // regular provider).
  const { data: veterinarians = [], isLoading: vetsLoading } = useQuery<Veterinarian[]>(
    'veterinarians',
    async () => {
      const response = await api.get('/users/veterinarians');
      return response.data;
    },
    // Refill intake is online-only (RxRefillRequest isn't a synced entity), so
    // gate every read with skipWhenOffline() to avoid doomed requests + error UI.
    { enabled: open && skipWhenOffline(), staleTime: 5 * 60_000 },
  );

  // Horse list (capped at 500 by the backend; Autocomplete searches client-side).
  const { data: horses = [], isLoading: horsesLoading } = useQuery(
    ['horses', 'all'],
    () => horseApi.list(),
    { enabled: open && !preselectedHorseId && skipWhenOffline(), staleTime: 60_000 },
  );

  // Resolve the preselected horse once the list is in (or via a direct fetch).
  const { data: preselectedHorse } = useQuery(
    ['horse', preselectedHorseId],
    () => horseApi.get(preselectedHorseId!),
    { enabled: open && !!preselectedHorseId && skipWhenOffline(), staleTime: 60_000 },
  );

  useEffect(() => {
    if (preselectedHorse && !selectedHorse) {
      setSelectedHorse(preselectedHorse);
    }
  }, [preselectedHorse, selectedHorse]);

  const horseId = selectedHorse?.id;

  // Take-home Rxs for the selected horse — staff picks the one the owner is
  // asking about. Filtering to isRx avoids cluttering with in-clinic records.
  const { data: medications = [], isLoading: medsLoading } = useQuery(
    ['medications', horseId],
    () => medicationService.getAll({ horseId, includeArchived: false }),
    { enabled: open && !!horseId && skipWhenOffline(), staleTime: 30_000 },
  );
  const rxMedications = useMemo(
    () => medications.filter((m) => m.isRx && !m.isArchived),
    [medications],
  );

  // Next upcoming appointment for this horse — light visit-context surfacing.
  const { data: upcomingAppointments = [] } = useQuery(
    ['appointments', 'upcoming', horseId],
    () =>
      appointmentApi.list({
        horseId,
        dateFrom: new Date().toISOString(),
      }),
    { enabled: open && !!horseId && skipWhenOffline(), staleTime: 30_000 },
  );
  const nextAppointment = useMemo(() => {
    if (upcomingAppointments.length === 0) return null;
    const sorted = [...upcomingAppointments].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
    );
    return sorted[0];
  }, [upcomingAppointments]);

  // Refill requests are online-only: RxRefillRequest isn't a synced entity and
  // approval involves server-side DEA C-II gating + clinic triage, so creation
  // is BLOCKED (not queued) offline rather than appearing to work.
  const offlineBlocked = !skipWhenOffline();
  const canSubmit =
    !!selectedHorse && rawIntakeBody.trim().length > 0 && !offlineBlocked;

  const handleSubmit = async () => {
    // Fail clearly offline — never route refill creation through the op queue.
    if (offlineBlocked) {
      setError('Refill requests require a connection — reconnect to log this request.');
      return;
    }
    if (!canSubmit || !selectedHorse) return;
    setSubmitting(true);
    setError(null);
    try {
      await refillRequestService.create({
        channel,
        horseId: selectedHorse.id,
        horseName: selectedHorse.name,
        medicationId: medicationId || undefined,
        assignedVetId: assignedVetId || undefined,
        ownerName: ownerName.trim() || undefined,
        ownerContact: ownerContact.trim() || undefined,
        rawIntakeBody: rawIntakeBody.trim() || undefined,
      });
      enqueueSnackbar('Refill request logged — routed to the vet queue', {
        variant: 'success',
      });
      onCreated?.();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to log refill request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Log refill request</DialogTitle>
      <DialogContent>
        <Stack spacing={2.25} sx={{ mt: 1 }}>
          {/* Channel — staff intake only. Email/SMS arrive via webhooks. */}
          <Box>
            <Typography variant="body2" sx={{ mb: 0.75, color: 'text.secondary' }}>
              How did the request come in?
            </Typography>
            <ToggleButtonGroup
              value={channel}
              exclusive
              size="small"
              fullWidth
              onChange={(_, val) => { if (val) setChannel(val); }}
            >
              <ToggleButton value="PHONE" sx={{ textTransform: 'none' }}>
                <PhoneIcon fontSize="small" sx={{ mr: 0.75 }} />
                Phone call
              </ToggleButton>
              <ToggleButton value="WALK_IN" sx={{ textTransform: 'none' }}>
                <WalkInIcon fontSize="small" sx={{ mr: 0.75 }} />
                Walk-in
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Horse — preselected when opened from the patient profile,
           * otherwise a searchable Autocomplete. */}
          {preselectedHorseId ? (
            <TextField
              label="Patient"
              size="small"
              value={selectedHorse?.name ?? '…'}
              disabled
              fullWidth
            />
          ) : (
            <Autocomplete
              options={horses}
              loading={horsesLoading}
              value={selectedHorse}
              onChange={(_, h) => {
                setSelectedHorse(h);
                setMedicationId(''); // reset Rx selection when horse changes
              }}
              getOptionLabel={patientLabel}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Patient"
                  size="small"
                  required
                  placeholder="Search by name or patient ID…"
                />
              )}
            />
          )}

          {/* Light visit context — helps staff/vet decide whether to log
           * the refill or roll it into an upcoming visit. */}
          {selectedHorse && nextAppointment && (
            <Alert
              severity="info"
              icon={<EventIcon fontSize="small" />}
              sx={{ py: 0.5 }}
            >
              <Typography variant="body2">
                <strong>Next appointment:</strong>{' '}
                <span data-testid="refill-log-next-visit">
                  {formatInstant(nextAppointment.date, NEXT_VISIT_FORMAT)}
                </span>
                {nextAppointment.type ? ` — ${nextAppointment.type}` : ''}
              </Typography>
            </Alert>
          )}

          {/* Medication — optional but recommended; pre-links the request to
           * a specific Rx so the vet skips the verification step. */}
          {selectedHorse && (
            <FormControl size="small" fullWidth>
              <InputLabel id="rx-medication-label">
                Which medication? (optional)
              </InputLabel>
              <Select
                labelId="rx-medication-label"
                label="Which medication? (optional)"
                value={medicationId}
                onChange={(e) => setMedicationId(String(e.target.value))}
                disabled={medsLoading || rxMedications.length === 0}
              >
                <MenuItem value="">
                  <em>Not specified — vet will pick the Rx</em>
                </MenuItem>
                {rxMedications.map((m) => {
                  const refillsAuthorized = m.refillsAuthorized ?? 0;
                  const totalFillsAllowed = refillsAuthorized + 1;
                  const fillsDispensed = m._count?.dispenseEvents ?? 0;
                  return (
                    <MenuItem key={m.id} value={m.id}>
                      {m.name}
                      {m.dosage ? ` (${m.dosage})` : ''}
                      {' '}— Fills {fillsDispensed}/{totalFillsAllowed}
                    </MenuItem>
                  );
                })}
              </Select>
              {selectedHorse && !medsLoading && rxMedications.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  This patient has no take-home Rxs on file. The vet can still
                  decide the request and link/issue an Rx.
                </Typography>
              )}
            </FormControl>
          )}

          {/* Assigned vet — optional routing hint. Tags the request so a
           * specific vet sees it in their queue (e.g. the owner's regular
           * provider). Any vet can still decide the request regardless. */}
          <FormControl size="small" fullWidth>
            <InputLabel id="assigned-vet-label">
              Assign to a vet (optional)
            </InputLabel>
            <Select
              labelId="assigned-vet-label"
              label="Assign to a vet (optional)"
              value={assignedVetId}
              onChange={(e) => setAssignedVetId(String(e.target.value))}
              disabled={vetsLoading || veterinarians.length === 0}
            >
              <MenuItem value="">
                <em>Unassigned — any vet can decide</em>
              </MenuItem>
              {veterinarians.map((v) => (
                <MenuItem key={v.id} value={v.id}>
                  {formatVetName(v)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              label="Owner name (optional)"
              size="small"
              value={ownerName}
              onChange={(e) => setOwnerName(e.target.value)}
            />
            <TextField
              label={channel === 'PHONE' ? 'Phone number' : 'Contact'}
              size="small"
              value={ownerContact}
              onChange={(e) => setOwnerContact(e.target.value)}
              placeholder={channel === 'PHONE' ? '(555) 123-4567' : ''}
            />
          </Box>

          <TextField
            label="What did the owner say?"
            placeholder={
              channel === 'PHONE'
                ? 'e.g. Wants a refill on Banamine, says they\'re running low and the horse is sore again'
                : 'e.g. Came in for more pain meds, horse is doing better but wants two more bottles'
            }
            multiline
            rows={3}
            size="small"
            value={rawIntakeBody}
            onChange={(e) => setRawIntakeBody(e.target.value)}
            required
          />

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Tooltip
          title={offlineBlocked ? 'Refill requests require a connection' : ''}
        >
          {/* span keeps the tooltip working while the button is disabled */}
          <span>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
              startIcon={
                submitting ? <CircularProgress size={16} color="inherit" /> : undefined
              }
            >
              {submitting ? 'Logging…' : 'Log request'}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default LogRefillRequestDialog;
